import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ADStatusEvent, AdPriorCompliance } from '@avisoma/shared';
import { CrossedOutCell } from './CrossedOutCell';
import { SourcePageCell } from './SourcePageCell';
import { Td, Th, TruncatedTd } from './cells';
import { FindingMarkerCell, FindingMarkerHeader, type RowFindings } from './FindingMarkerCell';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTheadClass,
} from './tableClasses';

type ADRow = ADStatusEvent & {
  pageId?: string;
  adPageIds?: string[];
  faaNumberConflict?: { numbers: string[] };
  priorCompliance?: AdPriorCompliance;
};

export function ADTable<T extends ADRow>({
  rows,
  pageIdToPosition,
  onInspect,
  rowFindings,
}: {
  rows: T[];
  pageIdToPosition?: Map<string, number>;
  onInspect: (row: T, index: number) => void;
  rowFindings?: RowFindings;
}) {
  // DR-5745: the column exists only when a row carries a marker, so a pack
  // with no part or paragraph label keeps the old header.
  const showRequirement = rows.some((row) => Boolean(row.requirement?.trim()));
  return (
    <table className={extractionTableClass}>
      <thead className={extractionTheadClass}>
        <tr>
          {rowFindings && <FindingMarkerHeader />}
          {pageIdToPosition && <Th>Page</Th>}
          <Th>AD Number</Th>
          {showRequirement && <Th>Requirement</Th>}
          <Th>Authority</Th>
          <Th>EU AD</Th>
          <Th>Revision</Th>
          <Th>Subject</Th>
          <Th>SB Ref</Th>
          <Th>Engine</Th>
          <Th>Effective</Th>
          <Th>Status</Th>
          <Th>Compliance</Th>
          <Th>Next due</Th>
          <Th>Repeat</Th>
          <Th>Remarks</Th>
        </tr>
      </thead>
      <tbody className={extractionTbodyClass}>
        {rows.map((r, i) => (
          <tr
            key={`${r.pageId}-${i}`}
            onClick={() => onInspect(r, i)}
            className={extractionRowClass}
          >
            {rowFindings && <FindingMarkerCell findings={rowFindings.get(i)} />}
            {pageIdToPosition && (
              <Td>
                <SourcePagesCell row={r} pageIdToPosition={pageIdToPosition} />
              </Td>
            )}
            <Td className="font-mono">
              <div className="flex items-start gap-1">
                <div className="min-w-0">
                  <CrossedOutCell text={r.adNumber} crossedOut={r.crossedOut} />
                </div>
                <FaaNumberConflictWarning conflict={r.faaNumberConflict} />
              </div>
            </Td>
            {showRequirement && <Td className="font-mono">{r.requirement?.trim() || null}</Td>}
            <Td className="font-mono">{r.issuingAuthority ?? '—'}</Td>
            <Td className="font-mono">{r.euad ?? '—'}</Td>
            <Td>{r.revision ?? '—'}</Td>
            <TruncatedTd value={r.subject} maxWidth="max-w-[240px]" />
            <Td className="font-mono">{r.sbReference ?? '—'}</Td>
            <Td>{r.applicableEngine ?? '—'}</Td>
            <Td>{r.effectiveDate ?? '—'}</Td>
            <Td>
              <StatusBadge
                status={r.status}
                remarksStatus={r.remarksStatus}
                remarksStatusReason={r.remarksStatusReason}
              />
              <NotOnLatestSheetBadge notOnLatestSheet={r.notOnLatestSheet} />
            </Td>
            <Td>
              {joinUnits([
                r.complianceDate,
                r.complianceTsn != null ? `TSN ${r.complianceTsn}` : undefined,
                r.complianceCsn != null ? `CSN ${r.complianceCsn}` : undefined,
                r.complianceShopVisit,
              ])}
              <PriorComplianceLine prior={r.priorCompliance} />
            </Td>
            <Td>
              {joinUnits([
                r.nextDueDate,
                r.nextDueHours != null ? `${r.nextDueHours} hrs` : undefined,
                r.nextDueCycles != null ? `${r.nextDueCycles} cyc` : undefined,
                r.nextDueShopVisit ?? (r.nextDueAtShopVisit ? 'shop visit' : undefined),
                r.remainingHours != null ? `${r.remainingHours} hrs remaining` : undefined,
                r.remainingCycles != null ? `${r.remainingCycles} cyc remaining` : undefined,
              ])}
            </Td>
            <Td>
              {joinUnits([
                r.repeatIntervalHours != null ? `${r.repeatIntervalHours} hrs` : undefined,
                r.repeatIntervalCycles != null ? `${r.repeatIntervalCycles} cyc` : undefined,
                r.repeatIntervalDays != null ? `${r.repeatIntervalDays} days` : undefined,
                r.repeatIntervalShopVisits != null ? `${r.repeatIntervalShopVisits} shop visits` : undefined,
              ])}
            </Td>
            <TruncatedTd value={r.remarks} />
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function joinUnits(parts: Array<string | undefined>): string {
  return parts.filter(Boolean).join(' · ') || '—';
}

/** DR-5051: an older document closed this AD; the newest one shows it open. */
function PriorComplianceLine({ prior }: { prior: ADRow['priorCompliance'] }) {
  if (!prior) return null;
  const clocks = [
    prior.complianceTsn != null ? `TSN ${prior.complianceTsn}` : undefined,
    prior.complianceCsn != null ? `CSN ${prior.complianceCsn}` : undefined,
    prior.complianceShopVisit,
  ]
    .filter(Boolean)
    .join(' · ');
  const label = ['Previously closed', prior.complianceDate, clocks ? `(${clocks})` : undefined]
    .filter(Boolean)
    .join(' ');
  return <div className="text-gray-500 dark:text-zinc-400">{label}</div>;
}

/** DR-5873: the newest AD sheet does not list this AD; the row keeps an older document's status. */
function NotOnLatestSheetBadge({ notOnLatestSheet }: { notOnLatestSheet: ADRow['notOnLatestSheet'] }) {
  if (notOnLatestSheet !== true) return null;
  return (
    <div className="mt-1">
      <span className="px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
        Not on latest AD sheet
      </span>
    </div>
  );
}

function SourcePagesCell({
  row,
  pageIdToPosition,
}: {
  row: ADRow;
  pageIdToPosition: Map<string, number>;
}) {
  const pageIds = row.adPageIds ?? (row.pageId ? [row.pageId] : []);
  if (pageIds.length === 0) return <>—</>;
  return (
    <span className="flex flex-col gap-0.5">
      {pageIds.map((pageId) => (
        <SourcePageCell key={pageId} pageId={pageId} pageIdToPosition={pageIdToPosition} />
      ))}
    </span>
  );
}

function StatusBadge({
  status,
  remarksStatus,
  remarksStatusReason,
}: {
  status: string | undefined;
  remarksStatus: ADStatusEvent['remarksStatus'];
  remarksStatusReason: ADStatusEvent['remarksStatusReason'];
}) {
  if (!status) return <>—</>;
  const tone =
    status === 'open'
      ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
      : status === 'closed'
        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200'
        : 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200';
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`px-1.5 py-0.5 rounded text-[10px] whitespace-nowrap ${tone}`}>{status}</span>
      <RemarksStatusWarning status={status} remarksStatus={remarksStatus} remarksStatusReason={remarksStatusReason} />
    </span>
  );
}

/**
 * DR-2597: the row's remarks declare a status that contradicts the status column. Advisory
 * only — the badge keeps showing the extracted status; this just marks the disagreement.
 * Re-checks the inequality rather than trusting the flag's presence: rows reach this table
 * through project-level merges, so a flag that ended up agreeing with the rendered status is
 * not a contradiction and must not be badged. The supporting remark is quoted in the tooltip
 * when the row carries it, so the disagreement can be judged without opening the source page.
 */
/**
 * DR-4271: two or more FAA biweekly numbers were read for this AD. Advisory only — this marks
 * the disagreement. DR-4348: the row's references keep ONE of them (the surviving document's);
 * the others live only on the flag, so the tooltip is where a dropped reading is still visible.
 */
function FaaNumberConflictWarning({ conflict }: { conflict: ADRow['faaNumberConflict'] }) {
  const numbers = conflict?.numbers;
  if (!numbers || numbers.length < 2) return null;
  const label = `Multiple FAA numbers — review source: ${numbers.join(', ')}`;
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className="inline-flex text-amber-600 dark:text-amber-400"
    >
      <ExclamationTriangleIcon className="size-3.5 shrink-0" />
    </span>
  );
}

function RemarksStatusWarning({
  status,
  remarksStatus,
  remarksStatusReason,
}: {
  status: string;
  remarksStatus: ADStatusEvent['remarksStatus'];
  remarksStatusReason: ADStatusEvent['remarksStatusReason'];
}) {
  if (!remarksStatus || remarksStatus === status) return null;
  const label = remarksStatusReason
    ? `Remarks suggest: ${remarksStatus} — "${remarksStatusReason}"`
    : `Remarks suggest: ${remarksStatus}`;
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className="inline-flex text-amber-600 dark:text-amber-400"
    >
      <ExclamationTriangleIcon className="size-3.5 shrink-0" />
    </span>
  );
}
